"use client";
import { useState, useEffect, createContext, useContext } from "react";
import Loading from "@/app/loading";

interface GoogleMapsProviderProps {
  children: React.ReactNode;
}

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: string | null;
  google: typeof window.google | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: null,
  google: null,
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

// Create a global variable to track the loading state
let isGoogleMapsInitializing = false;
let isGoogleMapsLoaded = false;

// Add this at the top of the file after the imports
declare global {
  interface Window {
    initGoogleMaps?: () => void;
    google?: typeof google;
  }
}

export default function GoogleMapsProvider({
  children,
}: GoogleMapsProviderProps) {
  const [isLoaded, setIsLoaded] = useState(isGoogleMapsLoaded);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [googleApi, setGoogleApi] = useState<typeof window.google | null>(null);

  // Function to load the Maps script
  const loadMapsScript = () => {
    // If already initializing, don't start another load
    if (isGoogleMapsInitializing) {
      return;
    }

    // Set flag to prevent concurrent loads
    isGoogleMapsInitializing = true;

    // Safety check - only proceed if script doesn't already exist
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    );
    if (existingScript) {
      isGoogleMapsInitializing = false;
      return;
    }

    // Create a new script element
    const script = document.createElement("script");
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    // Updated to use the latest version and ensure places library is loaded
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly&callback=initGoogleMaps&loading=async`;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-script";

    // Add error handler
    script.onerror = () => {
      console.error("Failed to load Google Maps script");
      setLoadError("Failed to load Google Maps");
      isGoogleMapsInitializing = false;
    };

    // Define the callback function
    window.initGoogleMaps = () => {
      try {
        if (window.google && window.google.maps && window.google.maps.places) {
          // Check if the new PlaceAutocompleteElement is available
          if (window.google.maps.places.PlaceAutocompleteElement) {
            isGoogleMapsLoaded = true;
            isGoogleMapsInitializing = false;
            setGoogleApi(window.google);
            setIsLoaded(true);
          } else {
            console.warn(
              "PlaceAutocompleteElement not available. This might be due to an older API version."
            );
            // Still mark as loaded for backwards compatibility
            isGoogleMapsLoaded = true;
            isGoogleMapsInitializing = false;
            setGoogleApi(window.google);
            setIsLoaded(true);
          }
        } else {
          console.error(
            "Google Maps callback called but Places library missing"
          );
          setLoadError("Failed to initialize Maps correctly");
          isGoogleMapsInitializing = false;
        }
      } catch (error) {
        console.error("Error during Google Maps initialization:", error);
        setLoadError("Error initializing Maps");
        isGoogleMapsInitializing = false;
      }
    };

    // Append the script to the document
    document.head.appendChild(script);
  };

  // Check if Google Maps is already loaded
  useEffect(() => {
    // First check if the API is already available
    if (window.google?.maps?.places) {
      isGoogleMapsLoaded = true;
      setGoogleApi(window.google);
      setIsLoaded(true);
      return;
    }

    // Then check if script is already in the DOM but not fully loaded
    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      return;
    }

    // Otherwise load the script
    if (!isGoogleMapsLoaded && !isGoogleMapsInitializing) {
      loadMapsScript();
    }

    return () => {
      // Don't delete the callback on unmount as it may be needed by other instances
    };
  }, []);

  return (
    <GoogleMapsContext.Provider
      value={{ isLoaded, loadError, google: googleApi }}
    >
      {!isLoaded ? <Loading /> : children}
    </GoogleMapsContext.Provider>
  );
}
